import React, { useState, useEffect, useRef } from 'react';
import { ProcessoViewModel } from '../../viewmodel/ProcessoViewModel';
import { StatusProcesso } from '../../model/processo/Processo';
import useUserStore from '../../stores/userStore';
import useUnidadeStore from '../../stores/unidadeStore';
import { UsuarioUtils } from '../../model/Usuario/Usuario';
import { PermissaoEnum } from '../../model/Usuario/Permissao';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import { createBreadcrumbs } from '../../utils/breadcrumbUtils';
import PermissaoWrapper from '../../components/PermissaoWrapper';
import CriarProcessoModal from './components/CriarProcesso';
import FiltrosProcessos from './components/FiltrosProcessos';
import TabelaProcessos from './components/TabelaProcessos';
import PaginacaoProcessos from './components/PaginacaoProcessos';
import HelpDrawerProcessos from './components/HelpDrawerProcessos';
import { BsQuestionCircle } from 'react-icons/bs';
import './index.css';

const ProcessosNova: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const usuario = useUserStore((state: any) => state.usuario);
  const { unidades, fetchUnidades } = useUnidadeStore();
  const [processos, setProcessos] = useState({ content: [], pageMetadata: { totalElements: 0, totalPages: 0, size: 10, number: 0 } });
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({ editalNome: '', identificador: '', responsavel: '', unidadeId: '', status: 'ATIVOS' as 'ATIVOS' | 'CANCELADOS' | 'FINALIZADOS' | '' });
  const [currentPage, setCurrentPage] = useState(0);
  const [showCriarModal, setShowCriarModal] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [visualizacaoDetalhada, setVisualizacaoDetalhada] = useState(false);
  const [showHelpDrawer, setShowHelpDrawer] = useState(false);
  const fetchPromiseRef = useRef<Promise<void> | null>(null);

  const temPermissao = usuario && UsuarioUtils.verificarTodasPermissoes(usuario, [PermissaoEnum.CONSULTAR_PROCESSO]);

  useEffect(() => {
    setBreadcrumbs(createBreadcrumbs.fromProcessos());
  }, [setBreadcrumbs]);

  useEffect(() => {
    if (temPermissao && !initialized) {
      setInitialized(true);
      fetchUnidades();
      fetchProcessos();
    }
  }, [temPermissao, initialized]);

  // Filtros com debounce
  useEffect(() => {
    if (!initialized) return;
    
    const timer = setTimeout(() => {
      setCurrentPage(0);
      fetchProcessos(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [filtros.editalNome, filtros.identificador, filtros.responsavel, filtros.unidadeId, filtros.status]);



  const fetchProcessos = async (page = currentPage) => {
    if (!temPermissao) return;
    
    const requestKey = `${page}-${JSON.stringify(filtros)}`;
    
    if (fetchPromiseRef.current) {
      return fetchPromiseRef.current;
    }
    
    const fetchPromise = (async () => {
      setLoading(true);
      try {
        const filtrosLimpos: any = Object.fromEntries(Object.entries(filtros).filter(([_, v]) => v !== ''));
        
        if (filtros.status === 'ATIVOS') {
          filtrosLimpos.status = 'ATIVO';
        } else if (filtros.status === 'CANCELADOS') {
          filtrosLimpos.status = StatusProcesso.CANCELADO;
        } else if (filtros.status === 'FINALIZADOS') {
          filtrosLimpos.status = StatusProcesso.FINALIZADO;
        }
        
        const result = await ProcessoViewModel.buscarProcessos(filtrosLimpos, page, 10);
        setProcessos(result);
      } catch (error) {
        console.error('Erro:', error);
        setProcessos({ content: [], pageMetadata: { totalElements: 0, totalPages: 0, size: 10, number: page } });
      } finally {
        setLoading(false);
        fetchPromiseRef.current = null;
      }
    })();
    
    fetchPromiseRef.current = fetchPromise;
    return fetchPromise;
  };

  const handleFiltroChange = (campo: string, valor: string) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchProcessos(newPage);
  };

  if (!temPermissao) {
    return (
      <div className="processos-container">
        <div className="alert alert-warning">Você não tem permissão para consultar processos.</div>
      </div>
    );
  }

  return (
    <HelpDrawerProcessos isOpen={showHelpDrawer} onClose={() => setShowHelpDrawer(false)}>
    <div className="processos-container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2>Processos</h2>
        <button
          className="btn-pagination"
          onClick={() => setShowHelpDrawer(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
          }}
          title="Abrir painel de ajuda"
        >
          <BsQuestionCircle /> Ajuda
        </button>
      </div>
      
      <FiltrosProcessos 
        filtros={filtros}
        unidades={unidades}
        onFiltroChange={handleFiltroChange}
        visualizacaoDetalhada={visualizacaoDetalhada}
        onToggleVisualizacao={() => setVisualizacaoDetalhada(!visualizacaoDetalhada)}
      />

      <TabelaProcessos 
        processos={processos.content}
        loading={loading}
        unidades={unidades}
        onProcessoUpdated={() => fetchProcessos(currentPage)}
        visualizacaoDetalhada={visualizacaoDetalhada}
      />
      
      <div className="pagination-container" style={{ justifyContent: 'space-between' }}>
        <PaginacaoProcessos 
          pageMetadata={processos.pageMetadata}
          onPageChange={handlePageChange}
        />
        
        <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.CADASTRAR_PROCESSO]}>
          <button 
            className="btn-pagination" 
            style={{ 
              backgroundColor: '#28a745', 
              color: 'white', 
              border: '1px solid #28a745',
              fontWeight: '500'
            }}
            onClick={() => setShowCriarModal(true)}
          >
            + Novo Processo
          </button>
        </PermissaoWrapper>
      </div>

      {showCriarModal && (
        <CriarProcessoModal
          unidades={unidades}
          onClose={() => setShowCriarModal(false)}
          onProcessoCriado={() => {
            setCurrentPage(0);
            fetchProcessos(0);
          }}
        />
      )}
    </div>
    </HelpDrawerProcessos>
  );
};

export default ProcessosNova;
